import * as React from 'react'
import { Layout } from '../components/Layout/Layout'
import { client } from '../utils/GQLClient'
import { GQLGrid } from '../containers/GQLGrid'
import { FACILITIES_GRID_QUERY } from '../data-access/facilities-grid-query'
import { Link, useNavigate } from 'react-router'
import { SubMenu } from '../components/SubMenu/SubMenu'
import LocationsCard from '../components/LocationsCard/LocationsCard'
import { DashboardHeader } from '../components/DashboardHeader/DashboardHeader'
import { TableRow as SemanticTableRow, TableCell } from 'semantic-ui-react'
import {
  facilitiesGrid_facilitiesGrid_items as FacilityGridItem,
  facilitiesGrid as FacilitiesGridData
} from '../data-access/__generated__/types'
import { Responsive } from '../components/Responsive/Responsive'
import { GridMemo } from '../components/GridMemo/GridMemo'
import InitialPropsContext from '../components/InitialPropsContext/InitialPropsContext'
import { Icons } from '@120wateraudit/envirio-components'

const IndexPage = () => {
  const { accountId, accountName, customContent } =
    React.useContext(InitialPropsContext)
  const navigate = useNavigate()

  if (!accountId || !accountName) {
    return null
  }

  return (
    <Layout title={accountName}>
      <SubMenu title="Water Quality Dashboard" accountName={accountName} />
      {!!customContent &&
      !!customContent.headline &&
      !!customContent.content ? (
        <DashboardHeader
          logo={customContent.logo}
          headline={customContent.headline}
          content={customContent.content}
        />
      ) : (
        ''
      )}
      <Responsive platform="desktop">
        <LocationsCard accountId={accountId} />
      </Responsive>
      <Responsive platform="desktop">
        <GQLGrid
          query={FACILITIES_GRID_QUERY}
          variables={{
            accountId
          }}
          client={client}
          availableFilters={{}}
          columns={columns}
          totalGetter={totalGetter}
          searchPlaceholder="Search Facilities..."
          entityName="Facility"
          pluralEntityName="Facilities"
          renderRow={renderRow(navigate, columns.length)}
          defaultSorts={{ schoolName: 'ASC' }}
        />
      </Responsive>
      <Responsive platform="mobile">
        <GridMemo
          content="Select a facility from the list below to view water sampling results at a detailed level."
          icon={<Icons.School />}
        />
        <GQLGrid
          query={FACILITIES_GRID_QUERY}
          variables={{
            accountId
          }}
          client={client}
          availableFilters={{}}
          columns={[columns[0]]}
          totalGetter={totalGetter}
          searchPlaceholder="Search Facilities..."
          entityName="Facility"
          pluralEntityName="Facilities"
          unstackable={true}
          renderRow={renderRow(navigate, 1)}
          defaultSorts={{ schoolName: 'ASC' }}
        />
      </Responsive>
    </Layout>
  )
}

// One renderer for both breakpoints: the mobile grid is the desktop grid with only
// the first column, and both rows navigate to the same URL. `detailsPath` is the
// literal URL Next's `as` prop produced, so bookmarked links keep working.
const detailsPath = (id: number, schoolName: string | null) =>
  `/details?facilityId=${id}&facilityName=${encodeURIComponent(schoolName ?? '')}`

const renderRow =
  (navigate: ReturnType<typeof useNavigate>, columnCount: number) =>
  ({ id, schoolName, address, city, state, zip }: FacilityGridItem) => (
    <TableRow key={id} onClick={() => navigate(detailsPath(id, schoolName))}>
      <TableCell>
        <Link to={detailsPath(id, schoolName)}>{schoolName}</Link>
      </TableCell>
      {columnCount > 1 && (
        <>
          <TableCell>{address}</TableCell>
          <TableCell>{city}</TableCell>
          <TableCell>{state}</TableCell>
          <TableCell>{zip}</TableCell>
        </>
      )}
    </TableRow>
  )

const TableRow = ({
  children,
  onClick
}: React.PropsWithChildren<{ onClick?: () => void }>) => (
  <SemanticTableRow className="parentTable" onClick={onClick}>
    {children}
  </SemanticTableRow>
)

const totalGetter = ({ facilitiesGrid }: FacilitiesGridData): number =>
  facilitiesGrid && facilitiesGrid.total ? facilitiesGrid.total : 0

const column = (name: string, key: string, sortable: boolean = false) => ({
  name,
  key,
  sortable
})

const columns = [
  column('Facility Name', 'schoolName', true),
  column('Street Address', 'address', true),
  column('City', 'city'),
  column('State', 'state'),
  column('Zip', 'zip')
]

export default IndexPage
